/**
 * =============================================================================
 * useWlRecentPayments
 *
 * Hook do painel WL → Financeiro. Feed cross-tenant de parcelas pagas
 * (últimos recebimentos), com scroll infinito via cursor composto
 * (data_pagamento, id). Consome a RPC `wl_list_recent_payments` v1.0.0.
 *
 * @version 1.0.0
 * RELEASE: 2026-07-21 v1.0.0 - Versao inicial.
 * =============================================================================
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/** Item devolvido pelo feed. */
export interface WlRecentPaymentItem {
  id: string;
  cobranca_id: string;
  descricao: string | null;
  valor_pago: number;
  data_pagamento: string;
  numero_parcela: number;
  numero_parcelas: number;
  cobranca_status: string;
  parcela_status: string;
  tenant_id: string;
  tenant_name: string;
  plan_name: string | null;
  meio_pagamento: string | null;
  tipo_key: string | null;
}

interface PageResponse {
  items: WlRecentPaymentItem[];
  has_more: boolean;
}

const PAGE_SIZE = 15;

/** Hook — feed de recebimentos cross-tenant (scroll infinito). */
export function useWlRecentPayments() {
  return useInfiniteQuery<PageResponse>({
    queryKey: ['wl-recent-payments'],
    staleTime: 60_000,
    initialPageParam: undefined,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as { date: string; id: string } | undefined;

      const { data, error } = await supabase.rpc('wl_list_recent_payments', {
        p_cursor_date: cursor?.date ?? null,
        p_cursor_id: cursor?.id ?? null,
        p_page_size: PAGE_SIZE,
      } as any);

      if (error) throw error;

      const payload = (data as unknown as PageResponse) ?? { items: [], has_more: false };
      return {
        items: payload.items ?? [],
        has_more: !!payload.has_more,
      };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage?.has_more || !lastPage.items?.length) return undefined;
      const last = lastPage.items[lastPage.items.length - 1];
      return { date: last.data_pagamento, id: last.id };
    },
  });
}
